import { Head, Link, router } from "@inertiajs/react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Plus,
  MoreHorizontal,
  Edit,
  Trash2,
  Landmark,
  Wallet,
  CalendarClock,
} from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import { liabilitiesPath, newLiabilityPath, liabilityPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Liabilities",
    href: liabilitiesPath(),
  },
]

interface Liability {
  id: number
  name: string
  category: string
  balance: number
  interest_rate: number | null
  minimum_payment: number | null
  months_to_payoff: number | null
  created_at: string
  updated_at: string
}

interface LiabilitiesIndexProps {
  liabilities: Liability[]
  categories: string[]
  summary: {
    total_balance: number
    total_minimum_payment: number
    liability_count: number
  }
}

export default function LiabilitiesIndex({
  liabilities,
  summary,
}: LiabilitiesIndexProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatPayoff = (months: number | null) => {
    if (months === null) return "—"
    if (months === 0) return "Paid off"
    const years = Math.floor(months / 12)
    const rem = months % 12
    if (years === 0) return `${rem} mo`
    return rem === 0 ? `${years} yr` : `${years} yr ${rem} mo`
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Liabilities" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Liabilities</h1>
            <p className="text-muted-foreground">
              Track debt to measure your true net worth
            </p>
          </div>
          <Button asChild>
            <Link href={newLiabilityPath()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Liability
            </Link>
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Debt</CardTitle>
              <Landmark className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(summary.total_balance)}
              </div>
              <p className="text-muted-foreground text-xs">
                {summary.liability_count} accounts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Monthly Minimums
              </CardTitle>
              <CalendarClock className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.total_minimum_payment || 0)}
              </div>
              <p className="text-muted-foreground text-xs">
                Total minimum payments
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Accounts</CardTitle>
              <Wallet className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {summary.liability_count}
              </div>
              <p className="text-muted-foreground text-xs">Open liabilities</p>
            </CardContent>
          </Card>
        </div>

        {/* Liabilities Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Liabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Min. Payment</TableHead>
                  <TableHead className="text-right">Payoff</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {liabilities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center">
                      <div className="text-muted-foreground">
                        No liabilities yet. Add debt to track your net worth
                        accurately.
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  liabilities.map((liability) => (
                    <TableRow key={liability.id}>
                      <TableCell>
                        <div className="font-medium">{liability.name}</div>
                        <div className="text-muted-foreground text-xs">
                          {format(
                            new Date(liability.created_at),
                            "MMM dd, yyyy",
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{liability.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-red-600 dark:text-red-400">
                        {formatCurrency(liability.balance)}
                      </TableCell>
                      <TableCell className="text-right">
                        {liability.interest_rate != null
                          ? `${liability.interest_rate}%`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {liability.minimum_payment != null
                          ? formatCurrency(liability.minimum_payment)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatPayoff(liability.months_to_payoff)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link href={`/liabilities/${liability.id}/edit`}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                if (
                                  confirm(
                                    "Are you sure you want to delete this liability?",
                                  )
                                ) {
                                  router.delete(liabilityPath(liability.id))
                                }
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
